/**
 * Paste special (W2-11 batch-tools; DESIGN.md §4.14, §5.4 step 4; RPE SEL-05..07): chooses HOW the clipboard is
 * pasted and returns the choice (DialogMap `pasteSpecial`); the paste itself is performed by the caller
 * (`edit.pasteSpecial`: a ghost at the mouse, or an immediate paste at the playhead / at the original beats).
 *  - mode: normal, add values onto the target curve (RPE Alt+V), as a new layer; mirrored (X := -X, values negated);
 *  - anchor: the mouse (ghost), the playhead, or the original beats;
 *  - targets: the target line, or the lines the items were copied from;
 *  - repeat N times every so many beats (stamping); X offset for notes.
 * A sentence summarises the result before it happens.
 */

import { useMemo, useState } from 'react';
import type { DialogProps } from '../../app/registry/types';
import type { PasteMode } from '../../core/commands/types';
import type { LineId } from '../../core/model/types';
import { Beats } from '../../core/time/beat';
import { formatBeat } from '../../core/time/beatSyntax';
import type { Beat } from '../../core/time/types';
import { Button } from '../../ui/Button';
import { Checkbox } from '../../ui/Checkbox';
import { Dialog } from '../../ui/Dialog';
import { Segmented } from '../../ui/Segmented';
import { Select } from '../../ui/Select';
import { m } from './batch.messages';
import s from './Batch.module.css';
import { BeatInput, NumberInput, useApplyOnEnter, useBeatContext, useText } from './sheetKit';

export type PasteKind = 'normal' | 'addValues' | 'asNewLayer';
export type PasteAnchor = 'mouse' | 'playhead' | 'original';

export interface PasteChoice {
  readonly kind: PasteKind;
  readonly mirror: boolean;
  readonly anchor: PasteAnchor;
  readonly toOriginalLines: boolean;
  readonly repeat: number;
  readonly every: Beat;
  readonly xShift: number;
}

/** The PasteMode of a choice. */
export function pasteModeOf(c: PasteChoice): PasteMode {
  return {
    mirror: c.mirror,
    addValues: c.kind === 'addValues',
    asNewLayer: c.kind === 'asNewLayer',
    originalBeats: c.anchor === 'original',
    toOriginalLines: c.toOriginalLines,
    repeat: c.repeat,
    ...(c.repeat > 1 ? { every: c.every } : {}),
  };
}

export function PasteSpecialDialog(p: DialogProps<'pasteSpecial'>) {
  const { services, props } = p;
  const text = useText();
  const store = services.store;
  const beatCtx = useBeatContext(services);
  const payload = props.payload;
  const multiLine = payload.lines.length > 1;
  const lines = useMemo(
    () =>
      store === null
        ? []
        : store.doc.lineOrder.map((id, i) => ({
            value: String(id),
            label: `#${String(i)} ${store.doc.lines.get(id)?.name ?? ''}`,
          })),
    [store],
  );
  const defaultEvery = Beats.lt(Beats.ZERO, payload.span) ? payload.span : Beats.ONE;
  const [choice, setChoice] = useState<PasteChoice>({
    kind: 'normal',
    mirror: false,
    anchor: services.settings.value.editing.pasteAnchor,
    toOriginalLines: false,
    repeat: 1,
    every: defaultEvery,
    xShift: 0,
  });
  const [target, setTarget] = useState<LineId>(props.targetLine);
  const set = (patch: Partial<PasteChoice>): void => {
    setChoice((c) => ({ ...c, ...patch }));
  };

  const hasKeys = payload.tracks.length > 0;
  const hasNotes = payload.notes.length > 0;
  const targetName = store?.doc.lines.get(target)?.name ?? '';
  const anchorText =
    choice.anchor === 'mouse'
      ? text.t(m.anchorMouseShort)
      : choice.anchor === 'playhead'
        ? text.t(m.anchorPlayheadShort)
        : text.t(m.anchorOriginalShort, { beat: formatBeat(payload.origin) });
  const summary = text.t(m.pasteSummary, {
    notes: payload.notes.length,
    keys: payload.tracks.reduce((n, t) => n + t.keys.length, 0),
    line: choice.toOriginalLines ? text.t(m.originalLines) : targetName,
    anchor: anchorText,
    repeat: choice.repeat,
    every: formatBeat(choice.every),
    x: choice.xShift,
    kind: choice.kind,
    mirror: choice.mirror ? 'yes' : 'no',
  });

  const paste = (): void => {
    const anchor = choice.anchor === 'original' ? payload.origin : choice.anchor;
    p.close({ mode: pasteModeOf(choice), anchor, targetLine: target, xShift: choice.xShift });
  };
  const everyInvalid = choice.repeat > 1 && !Beats.lt(Beats.ZERO, choice.every);
  const enter = useApplyOnEnter(paste, everyInvalid);

  return (
    <Dialog
      open
      size="md"
      title={text.t(m.pasteSpecialTitle)}
      onClose={() => {
        p.close(null);
      }}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              p.close(null);
            }}
          >
            {text.t(m.cancel)}
          </Button>
          <Button
            variant="primary"
            onClick={paste}
            disabledReason={everyInvalid ? { key: m.repeatEveryInvalid } : null}
          >
            {text.t(m.paste)}
          </Button>
        </>
      }
    >
      <div className={s.dialogBody} {...enter}>
        <Segmented<PasteKind>
          label={text.t(m.pasteMode)}
          value={choice.kind}
          options={[
            { value: 'normal', label: text.t(m.pasteNormal) },
            {
              value: 'addValues',
              label: text.t(m.pasteAddValues),
              ...(hasKeys ? {} : { disabledReason: { key: m.pasteNeedsKeys } }),
            },
            {
              value: 'asNewLayer',
              label: text.t(m.pasteNewLayer),
              ...(hasKeys ? {} : { disabledReason: { key: m.pasteNeedsKeys } }),
            },
          ]}
          onChange={(v) => {
            set({ kind: v });
          }}
        />
        {/* Why two modes are unavailable, said where it applies (UX-048), not only in their tooltips. */}
        {!hasKeys && <p className={s.hint}>{text.t(m.pasteNeedsKeys)}</p>}
        <Checkbox
          className={s.check}
          label={text.t(m.pasteMirror)}
          checked={choice.mirror}
          onChange={(v) => {
            set({ mirror: v });
          }}
        />
        <Segmented<PasteAnchor>
          label={text.t(m.pasteAnchor)}
          value={choice.anchor}
          options={[
            { value: 'mouse', label: text.t(m.anchorMouse) },
            { value: 'playhead', label: text.t(m.anchorPlayhead) },
            { value: 'original', label: text.t(m.anchorOriginal) },
          ]}
          onChange={(v) => {
            set({ anchor: v });
          }}
        />
        <div className={s.row}>
          <Select<string>
            label={text.t(m.pasteTargetLine)}
            value={String(target)}
            options={lines}
            onChange={(v) => {
              setTarget(Number(v) as LineId);
            }}
          />
          {multiLine && (
            <Segmented<'current' | 'original'>
              label={text.t(m.pasteTargets)}
              value={choice.toOriginalLines ? 'original' : 'current'}
              options={[
                { value: 'current', label: text.t(m.targetCurrent) },
                { value: 'original', label: text.t(m.targetOriginal) },
              ]}
              onChange={(v) => {
                set({ toOriginalLines: v === 'original' });
              }}
            />
          )}
        </div>
        <div className={s.row}>
          <NumberInput
            label={text.t(m.repeat)}
            value={choice.repeat}
            integer
            min={1}
            max={999}
            onChange={(v) => {
              set({ repeat: v });
            }}
          />
          <BeatInput
            label={text.t(m.repeatEvery)}
            value={choice.every}
            ctx={beatCtx}
            onChange={(b) => {
              set({ every: b });
            }}
          />
        </div>
        {hasNotes && (
          <NumberInput
            label={text.t(m.xOffset)}
            value={choice.xShift}
            unit={text.t(m.unitPx)}
            onChange={(v) => {
              set({ xShift: v });
            }}
          />
        )}
        <p className={s.summary} role="status" aria-live="polite">
          {summary}
        </p>
      </div>
    </Dialog>
  );
}
